import { useEffect, useState, type FormEvent } from "react";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";
import ServiceDeskAPIService from "@sd/services/APIs";

const NEW_PASSWORD_MIN = 8;
const NEW_PASSWORD_MAX = 10;

const PASSWORD_REQUIREMENTS = [
  `Between ${NEW_PASSWORD_MIN} and ${NEW_PASSWORD_MAX} characters`,
];

function firstErrorText(value: unknown): string | null {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (Array.isArray(value)) {
    for (const item of value) {
      const text = firstErrorText(item);
      if (text) return text;
    }
  }
  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return (
      firstErrorText(record.message) ??
      firstErrorText(record.detail) ??
      firstErrorText(record.error)
    );
  }
  return null;
}

function apiErrorMessage(error: unknown): string {
  if (!axios.isAxiosError(error)) {
    return "Could not reset password. Please try again.";
  }
  const data = error.response?.data;
  return (
    firstErrorText(data) ?? "Could not reset password. Please try again."
  );
}

function fieldErrorFromApi(
  error: unknown,
  field: "current_password" | "new_password"
): string | null {
  if (!axios.isAxiosError(error)) return null;
  const data = error.response?.data;
  if (!data || typeof data !== "object") return null;
  return firstErrorText((data as Record<string, unknown>)[field]);
}

export function ResetPasswordDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showRequirements, setShowRequirements] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{
    current?: string;
    next?: string;
    form?: string;
  }>({});

  const resetForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setShowCurrent(false);
    setShowNew(false);
    setShowRequirements(false);
    setSubmitting(false);
    setErrors({});
  };

  useEffect(() => {
    if (!open) resetForm();
  }, [open]);

  const validate = () => {
    const next: { current?: string; next?: string } = {};
    if (!currentPassword.trim()) {
      next.current = "Current password is required.";
    }
    const trimmedNew = newPassword.trim();
    if (!trimmedNew) {
      next.next = "New password is required.";
    } else if (
      trimmedNew.length < NEW_PASSWORD_MIN ||
      trimmedNew.length > NEW_PASSWORD_MAX
    ) {
      next.next = `New password must be ${NEW_PASSWORD_MIN}–${NEW_PASSWORD_MAX} characters.`;
    } else if (trimmedNew === currentPassword.trim()) {
      next.next = "New password must be different from the current password.";
    }
    setErrors(next);
    return !next.current && !next.next;
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (submitting || !validate()) return;

    setSubmitting(true);
    setErrors({});
    try {
      await ServiceDeskAPIService.auth.changePassword(
        {
          current_password: currentPassword,
          new_password: newPassword,
        },
        { skipErrorNotification: true }
      );
      toast.success("Password reset successfully");
      onOpenChange(false);
    } catch (error) {
      setErrors({
        current: fieldErrorFromApi(error, "current_password") ?? undefined,
        next: fieldErrorFromApi(error, "new_password") ?? undefined,
        form:
          fieldErrorFromApi(error, "current_password") ||
          fieldErrorFromApi(error, "new_password")
            ? undefined
            : apiErrorMessage(error),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[min(440px,calc(100vw-2rem))] gap-0 p-0 sm:rounded-xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader className="space-y-1.5 px-6 pb-4 pt-6 text-left">
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Enter your current password and a new password to change your
              password.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 px-6 pb-2">
            <div className="space-y-1.5">
              <Label htmlFor="sd-current-password">Current Password</Label>
              <div className="relative">
                <Input
                  id="sd-current-password"
                  type={showCurrent ? "text" : "password"}
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={e => {
                    setCurrentPassword(e.target.value);
                    if (errors.current) {
                      setErrors(prev => ({ ...prev, current: undefined }));
                    }
                  }}
                  className={cn(
                    "pr-10",
                    errors.current && "border-destructive focus-visible:ring-destructive"
                  )}
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowCurrent(v => !v)}
                  aria-label={showCurrent ? "Hide current password" : "Show current password"}
                >
                  {showCurrent ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.current ? (
                <p className="text-xs text-destructive">{errors.current}</p>
              ) : null}
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="sd-new-password">New Password</Label>
                <button
                  type="button"
                  className="text-xs font-medium text-primary hover:underline"
                  onClick={() => setShowRequirements(v => !v)}
                >
                  {showRequirements ? "Hide Requirements" : "Show Requirements"}
                </button>
              </div>
              {showRequirements ? (
                <ul className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
                  {PASSWORD_REQUIREMENTS.map(rule => (
                    <li key={rule}>• {rule}</li>
                  ))}
                </ul>
              ) : null}
              <div className="relative">
                <Input
                  id="sd-new-password"
                  type={showNew ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder={`Enter new password (${NEW_PASSWORD_MIN}-${NEW_PASSWORD_MAX} characters)`}
                  value={newPassword}
                  onChange={e => {
                    setNewPassword(e.target.value);
                    if (errors.next) {
                      setErrors(prev => ({ ...prev, next: undefined }));
                    }
                  }}
                  maxLength={NEW_PASSWORD_MAX}
                  className={cn(
                    "pr-10",
                    errors.next && "border-destructive focus-visible:ring-destructive"
                  )}
                  disabled={submitting}
                />
                <button
                  type="button"
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowNew(v => !v)}
                  aria-label={showNew ? "Hide new password" : "Show new password"}
                >
                  {showNew ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
              {errors.next ? (
                <p className="text-xs text-destructive">{errors.next}</p>
              ) : null}
            </div>

            {errors.form ? (
              <p className="text-xs text-destructive">{errors.form}</p>
            ) : null}
          </div>

          <DialogFooter className="gap-2 px-6 py-4 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              disabled={submitting}
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Resetting…
                </>
              ) : (
                "Reset Password"
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
